import {
  filterDuplicateSlots,
  getSlotPayload,
  isSlotEqual
} from '@maxpreps/shared/functions/utils/adUtils';
import { AnyAction } from 'redux';
import { IBidBarrelStore, PageState } from '../reduxTypes';
import { ISlotPayload } from './bidBarrelHooks';

const DEFAULT_BIDBARREL_STORE: IBidBarrelStore = {
  pageState: PageState.Prehydrated,
  isInitialized: false,
  bidBarrelData: undefined,
  slots: [],
  auctionSlots: [],
  isAutomaticPageAuctionComplete: false
};

declare namespace BidBarrel {
  const destroySlots: (units: ISlotPayload[]) => void;
}

export enum BidBarrelStoreAction {
  /**
   * Update store with the state of the page. Managed by useInitializeBidBarrel
   */
  UpdatePageState = 'UpdatePageState',
  /**
   * Update store with the state of the BidBarrel API. Managed by useEnableBidBarrel
   */
  SetInitialized = 'SetInitialized',
  /**
   * Called by <AdUnit to get a new ad in the hopper for auctioning
   */
  DefineAd = 'DefineAd',
  /**
   * Called by <AdUnit to have it's references in the BidBarrel API torn down
   */
  DestroyAd = 'DestroyAd',
  /**
   * Ads items to the auctionSlot list.
   * Useful to force a page to reload all ads
   */
  Auction = 'Auction',

  /**
   * Remove all items from the auctionSlots list
   */
  ClearAuctionSlots = 'ClearAuctionSlots',

  /**
   * Auction everything in the hopper once the page and BB are all prepped for it.
   * This is a one-time auto-managed auction on every SSR or CSR page load
   */
  AutomaticPageAuctionComplete = 'AutomaticPageAuctionComplete'
}

const bidBarrelReducer = (
  state: IBidBarrelStore = DEFAULT_BIDBARREL_STORE,
  action: AnyAction
) => {
  switch (action.type) {
    case BidBarrelStoreAction.SetInitialized:
      return { ...state, isInitialized: true };

    case BidBarrelStoreAction.UpdatePageState:
      return {
        ...state,
        pageState: action.payload,
        isAutomaticPageAuctionComplete:
          // use whatever isAutomaticPageAuctionComplete already is UNLESS the payload says it's transitoning - then set it to false
          state.isAutomaticPageAuctionComplete &&
          action.payload !== PageState.Transitioning
      };

    case BidBarrelStoreAction.DefineAd: {
      const isDuplicate = state.slots.some(item =>
        isSlotEqual(item, action.payload)
      );

      if (isDuplicate) return state;

      const newSlots = [...state.slots, { ...action.payload }];

      const auctionSlots = state.isAutomaticPageAuctionComplete
        ? [...state.auctionSlots, { ...action.payload }]
        : state.auctionSlots;

      return {
        ...state,
        slots: newSlots,
        auctionSlots: auctionSlots
      };
    }

    case BidBarrelStoreAction.DestroyAd: {
      const filteredSlots = state.slots.filter(
        item => !isSlotEqual(item, action.payload)
      );

      const filteredAuctionSlots = state.auctionSlots.filter(
        item => !isSlotEqual(item, action.payload)
      );

      // this is a side-effect. making this not pure.
      // maybe want to refactor if running into weirdness
      // (Reminder - sometimes dispatches are ran twice - that's where weirdness could be introduced)
      // TODO: Have state manage a destroySlots list that is handled by bidBarrelHooks, much like auctionSlots are
      if (BidBarrel?.destroySlots) {
        //console.log('BidBarrel', 'Destorying slots (Reducer)');
        const destroySlots = getSlotPayload([action.payload]);
        BidBarrel.destroySlots(destroySlots);
      }

      return {
        ...state,
        slots:
          state.slots.length === filteredSlots.length
            ? state.slots
            : filteredSlots,
        auctionSlots:
          state.auctionSlots.length === filteredAuctionSlots.length
            ? state.auctionSlots
            : filteredAuctionSlots
      };
    }

    // this clears all but we may not have auctions all...
    // it seems to be working but if we notice that it's clearing items that haven't been auctioned yet
    // then we simple need to be more thoughtful about which items we clear from auctionSlots. Only
    // clear items that are in the payload of items to clear.
    // Note: Payload is being isgnored BUT it's filled with the items that were just auctioned (or undefined if a new pageview caused the clear autction)
    case BidBarrelStoreAction.ClearAuctionSlots: {
      return {
        ...state,
        auctionSlots: state.auctionSlots.length == 0 ? state.auctionSlots : []
      };
    }

    case BidBarrelStoreAction.AutomaticPageAuctionComplete: {
      return {
        ...state,
        isAutomaticPageAuctionComplete: true,
        auctionSlots: state.auctionSlots.length == 0 ? state.auctionSlots : []
      };
    }

    case BidBarrelStoreAction.Auction: {
      let slots;

      // auction all
      if ((action.payload?.length ?? 0) === 0) {
        slots = [...state.slots];
      }
      // find specific items
      else {
        slots = state.slots.filter(slot =>
          action.payload.some(payloadItem => isSlotEqual(payloadItem, slot))
        );
      }

      return {
        ...state,
        auctionSlots: filterDuplicateSlots([...state.auctionSlots, ...slots])
      };
    }

    default: {
      return state;
    }
  }
};

export default bidBarrelReducer;
